/**
 * PublishSuccessModal — pojawia się po opublikowaniu hodowli z Wizard V2.
 *
 * Trigger: query param `?published={slug}` w /panel.
 * Fetch: pobiera dane hodowli z /api/kennels/by-slug/{slug} aby pokazać podgląd + CTA share.
 * Share kanały: WhatsApp, Facebook, Messenger, Instagram (copy), Email, Copy link.
 *
 * UX: confetti przy otwarciu, fioletowy gradient, duży CTA "Zobacz stronę".
 */
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, ExternalLink, Copy, Check, Share2, MessageSquare,
  Facebook, Instagram, Mail, MessageCircle, Rocket, Sparkles, Globe,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { firePawConfetti } from '@/utils/confetti';
import { API } from '@/utils/api';
import { getImageUrl } from '@/utils/imageUrl';

const PUBLIC_BASE = typeof window !== 'undefined' ? window.location.origin : '';

export default function PublishSuccessModal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const slug = searchParams.get('published');
  const [kennel, setKennel] = useState(null);
  const [copied, setCopied] = useState(false);

  const shareUrl = slug ? `${PUBLIC_BASE}/hodowla/${slug}` : '';

  useEffect(() => {
    if (!slug) {
      setKennel(null);
      return;
    }
    // Fire confetti the moment the modal opens
    try { firePawConfetti(); } catch {}
    // Fetch kennel preview from user's own kennels (just published — so it's in their list)
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/kennels`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = Array.isArray(res.data) ? res.data : [];
        const match = list.find((k) => k.slug === slug);
        if (match) setKennel(match);
      } catch {}
    })();
  }, [slug]);

  if (!slug) return null;

  const close = () => {
    searchParams.delete('published');
    setSearchParams(searchParams, { replace: true });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link skopiowany!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Nie udało się skopiować');
    }
  };

  const shareText = kennel?.name
    ? `🎉 Mój profil hodowli "${kennel.name}" jest teraz online! Zobacz: ${shareUrl}`
    : `🎉 Moja hodowla jest teraz online! Zobacz: ${shareUrl}`;

  const channels = [
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageSquare,
      color: 'bg-emerald-500 hover:bg-emerald-600',
      href: `https://wa.me/?text=${encodeURIComponent(shareText)}`,
    },
    {
      id: 'facebook',
      label: 'Facebook',
      icon: Facebook,
      color: 'bg-blue-600 hover:bg-blue-700',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: 'messenger',
      label: 'Messenger',
      icon: MessageCircle,
      color: 'bg-sky-500 hover:bg-sky-600',
      href: `https://www.facebook.com/dialog/send?link=${encodeURIComponent(shareUrl)}&app_id=140586622674265&redirect_uri=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: 'email',
      label: 'Email',
      icon: Mail,
      color: 'bg-gray-700 hover:bg-gray-800',
      href: `mailto:?subject=${encodeURIComponent('Moja hodowla jest online')}&body=${encodeURIComponent(shareText)}`,
    },
  ];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-in fade-in duration-200"
      data-testid="publish-success-modal"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Zamknij"
        onClick={close}
        className="absolute inset-0 bg-gray-900/70 backdrop-blur-sm"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        {/* Close */}
        <button
          type="button"
          onClick={close}
          data-testid="publish-success-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 hover:bg-gray-100 flex items-center justify-center text-gray-500 z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Hero gradient */}
        <div className="relative px-6 sm:px-8 pt-8 pb-6 bg-gradient-to-br from-violet-600 via-violet-500 to-fuchsia-500 text-white rounded-t-3xl overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-fuchsia-300/20 rounded-full blur-2xl" />

          <div className="relative">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/15 backdrop-blur rounded-full text-xs font-semibold mb-3">
              <Sparkles className="w-3.5 h-3.5" />
              Profil opublikowany
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold leading-tight">
              Gratulacje! Twoja hodowla jest live 🎉
            </h2>
            <p className="text-sm sm:text-base text-violet-100 mt-2">
              Od tej chwili klienci mogą Cię znaleźć w Katalogu APISAI i Google.
            </p>
          </div>
        </div>

        {/* Preview card */}
        <div className="px-5 sm:px-8 -mt-3">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-md overflow-hidden">
            <div className="flex flex-col sm:flex-row">
              <div className="w-full sm:w-48 h-24 sm:h-auto bg-gray-100 flex-shrink-0">
                {kennel?.cover_image ? (
                  <img
                    src={getImageUrl(kennel.cover_image) || kennel.cover_image}
                    alt={kennel.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-violet-100 to-fuchsia-100 flex items-center justify-center">
                    <Globe className="w-8 h-8 text-violet-400" />
                  </div>
                )}
              </div>
              <div className="flex-1 p-3 sm:p-4 min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">apisai.pl</p>
                <p className="font-bold text-gray-900 truncate text-sm sm:text-base" data-testid="publish-success-kennel-name">
                  {kennel?.name || 'Twoja hodowla'}
                </p>
                {kennel?.city && (
                  <p className="text-xs text-gray-500 mt-0.5">{kennel.city}{kennel.voivodeship ? `, ${kennel.voivodeship}` : ''}</p>
                )}
                <p className="text-xs sm:text-sm text-gray-600 mt-1.5 line-clamp-2">
                  {kennel?.description || 'Strona Twojej hodowli z opisem, zdjęciami i kontaktem.'}
                </p>
              </div>
            </div>

            {/* Link bar */}
            <div className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-gray-50 border-t border-gray-200">
              <span className="text-[10px] sm:text-xs text-gray-600 truncate flex-1 font-mono">{shareUrl}</span>
              <button
                type="button"
                onClick={handleCopy}
                data-testid="publish-success-copy-link"
                className="flex-shrink-0 px-2 py-1 bg-white hover:bg-gray-100 border border-gray-200 rounded-md text-[10px] sm:text-xs font-medium text-gray-700 flex items-center gap-1"
              >
                {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                {copied ? 'Skopiowane' : 'Kopiuj'}
              </button>
            </div>
          </div>
        </div>

        {/* Share channels */}
        <div className="px-5 sm:px-8 py-4 sm:py-6">
          <p className="text-xs sm:text-sm font-semibold text-gray-900 mb-2.5 flex items-center gap-2">
            <Share2 className="w-4 h-4 text-violet-600" />
            Podziel się z klientami i znajomymi
          </p>

          <div className="grid grid-cols-4 gap-1.5">
            {channels.map(({ id, label, icon: Icon, color, href }) => (
              <a
                key={id}
                href={href}
                target="_blank"
                rel="noreferrer noopener"
                data-testid={`publish-success-share-${id}`}
                className={`flex flex-col items-center gap-1 py-2 sm:py-3 rounded-xl text-white font-medium text-[10px] sm:text-xs ${color} transition-all hover:-translate-y-0.5 hover:shadow-lg`}
              >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                {label}
              </a>
            ))}
          </div>

          {/* Instagram special — copy text + open app */}
          <button
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(shareText);
                toast.success('Tekst skopiowany — wklej w Instagramie');
                window.open('https://www.instagram.com/', '_blank', 'noreferrer');
              } catch { toast.error('Nie udało się skopiować'); }
            }}
            data-testid="publish-success-share-instagram"
            className="w-full mt-2 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 text-white font-medium text-xs hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            <Instagram className="w-4 h-4" />
            Skopiuj tekst i otwórz Instagram
          </button>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-2 mt-5 pt-5 border-t border-gray-100">
            <Button
              type="button"
              onClick={() => window.open(shareUrl, '_blank', 'noreferrer')}
              data-testid="publish-success-view-page"
              className="flex-1 bg-violet-600 hover:bg-violet-700 text-white shadow-md shadow-violet-200/60"
            >
              <ExternalLink className="w-4 h-4 mr-1.5" />
              Zobacz swoją stronę
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                close();
                navigate('/panel/promocja');
              }}
              data-testid="publish-success-promote"
              className="flex-1 border-violet-200 text-violet-700 hover:bg-violet-50"
            >
              <Rocket className="w-4 h-4 mr-1.5" />
              Zaplanuj promocję
            </Button>
          </div>

          <p className="text-xs text-gray-400 text-center mt-4">
            Możesz wrócić do tego okna z panelu — przycisk „Udostępnij" przy Twojej hodowli.
          </p>
        </div>
      </div>
    </div>
  );
}
